import { Button } from '@/components/ui/button'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import { ClockCircle } from '@mynaui/icons-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { FC, ReactNode, createContext, useContext, useEffect, useReducer, useRef } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { BackgroundGradientAnimation } from './AnimatedCosGradientBg'
import { Text } from './custom-components'

const FOCUS_DURATION_MS = 40 * 60 * 1000 // 40m
const BREAK_DURATION_MS = 10 * 60 * 1000 // 10m

type State = {
  isOverlayVisible: boolean
  isBreakStarted: boolean
  countdown: number
  storedBreakStartTime: number | null
}

type Action =
  | { type: 'START_FOCUS' }
  | { type: 'SHOW_OVERLAY' }
  | { type: 'START_BREAK'; timestamp: number }
  | { type: 'TICK'; remaining: number }
  | { type: 'END_BREAK' }
  | { type: 'DISMISS' }

const initialState: State = {
  isOverlayVisible: false,
  isBreakStarted: false,
  countdown: BREAK_DURATION_MS,
  storedBreakStartTime: null,
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'START_FOCUS':
      return {
        ...state,
        isOverlayVisible: false,
        isBreakStarted: false,
        storedBreakStartTime: null,
        countdown: BREAK_DURATION_MS,
      }
    case 'SHOW_OVERLAY':
      return {
        ...state,
        isOverlayVisible: true,
        isBreakStarted: false,
        countdown: BREAK_DURATION_MS,
      }
    case 'START_BREAK':
      return {
        ...state,
        isOverlayVisible: true,
        isBreakStarted: true,
        storedBreakStartTime: action.timestamp,
      }
    case 'TICK':
      return { ...state, countdown: action.remaining }
    case 'END_BREAK':
    case 'DISMISS':
      return {
        ...state,
        isOverlayVisible: false,
        isBreakStarted: false,
        storedBreakStartTime: null,
        countdown: BREAK_DURATION_MS,
      }
    default:
      return state
  }
}

interface BrainBreakContextType {
  isOverlayVisible: boolean
  countdown: number
  startBreak: () => void
  dismiss: () => void
}

const BrainBreakContext = createContext<BrainBreakContextType | undefined>(undefined)
export const useBrainBreak = () => {
  const ctx = useContext(BrainBreakContext)
  if (!ctx) throw new Error('useBrainBreak must be used within BrainBreakProvider')
  return ctx
}

interface Props {
  children: ReactNode
}

export const BrainBreakProvider: FC<Props> = ({ children }) => {
  const teamsFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.brainBreak)

  // persist break info
  const [storedCountdown, setStoredCountdown] = useLocalStorage<number>('brain-break-countdown', BREAK_DURATION_MS)
  const [storedBreakStartTime, setStoredBreakStartTime] = useLocalStorage<number | null>('brain-break-start-time', null)
  // NEW: persist focus start
  const [storedFocusStartTime, setStoredFocusStartTime] = useLocalStorage<number | null>(
    'brain-break-focus-start-time',
    null,
  )

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    countdown: storedCountdown,
    storedBreakStartTime,
  })

  const focusTimer = useRef<number | null>(null)
  const breakTimer = useRef<number | null>(null)

  const clearFocusTimer = () => {
    if (focusTimer.current) {
      clearTimeout(focusTimer.current)
      focusTimer.current = null
    }
    setStoredFocusStartTime(null)
  }

  const startFocusTimer = () => {
    if (!teamsFlagEnabled) return

    const now = Date.now()
    // schedule next SHOW_OVERLAY at remaining focus time
    let remaining = FOCUS_DURATION_MS
    if (storedFocusStartTime) {
      const elapsed = now - storedFocusStartTime
      remaining = Math.max(0, FOCUS_DURATION_MS - elapsed)
    } else {
      setStoredFocusStartTime(now)
    }

    clearFocusTimer()
    focusTimer.current = window.setTimeout(() => {
      dispatch({ type: 'SHOW_OVERLAY' })
    }, remaining)

    // reset any break state
    dispatch({ type: 'START_FOCUS' })
  }

  const startBreak = () => {
    if (!teamsFlagEnabled) return
    if (breakTimer.current) clearInterval(breakTimer.current)

    const now = Date.now()
    dispatch({ type: 'START_BREAK', timestamp: now })
    setStoredBreakStartTime(now)
    clearFocusTimer()

    breakTimer.current = window.setInterval(() => {
      const elapsed = Date.now() - now
      const remaining = BREAK_DURATION_MS - elapsed

      if (remaining <= 0) {
        clearInterval(breakTimer.current!)
        dispatch({ type: 'END_BREAK' })
        setStoredBreakStartTime(null)
        startFocusTimer()
      } else {
        dispatch({ type: 'TICK', remaining })
      }
    }, 1000)
  }

  const dismiss = () => {
    if (focusTimer.current) clearTimeout(focusTimer.current)
    if (breakTimer.current) clearInterval(breakTimer.current)
    dispatch({ type: 'DISMISS' })
    setStoredBreakStartTime(null)
    startFocusTimer()
  }

  // persist countdown & break start
  useEffect(() => {
    setStoredCountdown(state.countdown)
    setStoredBreakStartTime(state.storedBreakStartTime)
  }, [state.countdown, state.storedBreakStartTime])

  // on mount or feature-flag change
  useEffect(() => {
    if (!teamsFlagEnabled) return

    const now = Date.now()

    if (storedBreakStartTime) {
      // resume break
      const elapsed = now - storedBreakStartTime
      const remaining = BREAK_DURATION_MS - elapsed

      if (remaining > 0) {
        dispatch({ type: 'START_BREAK', timestamp: storedBreakStartTime })
        dispatch({ type: 'TICK', remaining })

        breakTimer.current = window.setInterval(() => {
          const newElapsed = Date.now() - storedBreakStartTime
          const newRemaining = BREAK_DURATION_MS - newElapsed

          if (newRemaining <= 0) {
            clearInterval(breakTimer.current!)
            dispatch({ type: 'END_BREAK' })
            setStoredBreakStartTime(null)
            startFocusTimer()
          } else {
            dispatch({ type: 'TICK', remaining: newRemaining })
          }
        }, 1000)
      } else {
        // break already expired
        dispatch({ type: 'END_BREAK' })
        setStoredBreakStartTime(null)
        startFocusTimer()
      }
    } else {
      // start or resume focus
      startFocusTimer()
    }

    return () => {
      clearFocusTimer()
      if (breakTimer.current) clearInterval(breakTimer.current)
    }
  }, [teamsFlagEnabled])

  return (
    <BrainBreakContext.Provider
      value={{ isOverlayVisible: state.isOverlayVisible, countdown: state.countdown, startBreak, dismiss }}
    >
      {children}
      {teamsFlagEnabled && <BrainBreakOverlay isBreakStarted={state.isBreakStarted} />}
    </BrainBreakContext.Provider>
  )
}

const BrainBreakOverlay: FC<{ isBreakStarted: boolean }> = () => {
  const { isOverlayVisible, countdown, startBreak, dismiss } = useBrainBreak()
  const progress = 100 - (countdown / BREAK_DURATION_MS) * 100
  const formatTime = (ms: number) => {
    const m = Math.floor(ms / 60000)
    const s = Math.floor((ms % 60000) / 1000)
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  return (
    <AnimatePresence>
      {isOverlayVisible && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-transparent"
        >
          <motion.div
            key="screen"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 1 }}
            className="w-screen h-screen backdrop-blur-md rounded-2xl text-center flex items-center justify-center"
          >
            <BackgroundGradientAnimation>
              {countdown === BREAK_DURATION_MS ? (
                <>
                  <h2 className="text-2xl font-semibold mb-4">Time for a Brain Break 🧠</h2>
                  <ClockCircle className="w-16 h-16" />
                  <Text className="mb-4 text-thememutedforeground text-justify">
                    <strong>You've been focused for a while. Great work!</strong> But even the sharpest minds need time
                    to recharge. Take 10 minutes for yourself. <strong>Breathe. Stretch. Reset.</strong> Your creativity
                    will thank you. 🧠 🎉
                  </Text>
                  <div className="flex flex-col gap-2 w-full">
                    <Button onClick={startBreak} className="w-full">
                      Start Break
                    </Button>
                    <Button variant="outline" onClick={dismiss} className="w-full">
                      Dismiss
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold mb-4">Break in progress…</h2>
                  <div className="relative w-56 h-56 mb-6">
                    <svg viewBox="0 0 224 224" style={{ transform: 'rotate(-90deg)' }} className="w-full h-full">
                      <circle cx="112" cy="112" r="98" stroke="#e5e7eb" strokeWidth="16" fill="none" />
                      <circle
                        cx="112"
                        cy="112"
                        r="98"
                        stroke="#6366f1"
                        strokeWidth="16"
                        fill="none"
                        strokeDasharray={2 * Math.PI * 98}
                        strokeDashoffset={2 * Math.PI * 98 * (1 - progress / 100)}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dashoffset 0.5s linear' }}
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center gap-3">
                      <ClockCircle className="w-7 h-7" />
                      <span className="text-2xl font-mono select-none">{formatTime(countdown)}</span>
                    </span>
                  </div>
                  <Button variant="secondary" onClick={dismiss} className="w-full">
                    Skip Remaining
                  </Button>
                </>
              )}
            </BackgroundGradientAnimation>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
